import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { ClockCounterClockwise, Trash, FileXls } from "@phosphor-icons/react";

export default function LeadImportHistoryDialog({ onChanged }) {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/leads/import-history")
      .then((r) => setRows(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const undo = async (b) => {
    if (!window.confirm(`Anulezi importul „${b.filename}"? Se vor șterge ${b.count} lead-uri create atunci.`)) return;
    setBusyId(b.id);
    try {
      const { data } = await api.delete(`/leads/import-batch/${b.id}`);
      toast.success(`Import anulat — ${data.removed} lead-uri șterse`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const fmtDate = (s) => { try { return new Date(s).toLocaleString("ro-RO"); } catch { return s; } };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="import-history-btn">
          <ClockCounterClockwise size={16} weight="bold" className="mr-1.5" /> Istoric importuri
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Istoric importuri Excel</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <p className="py-8 text-center text-sm text-muted-foreground">Se încarcă…</p>}
          {!loading && rows.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground" data-testid="import-history-empty">Niciun import încă.</p>
          )}
          {!loading && rows.length > 0 && (
            <div className="divide-y divide-border">
              {rows.map((b) => (
                <div key={b.id} className="flex items-center gap-3 py-3" data-testid={`import-batch-${b.id}`}>
                  <FileXls size={26} className={b.undone ? "text-muted-foreground" : "text-emerald-400"} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold">{b.filename}</span>
                      {b.undone && <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">ANULAT</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {b.count} lead-uri{b.skipped ? ` • ${b.skipped} sărite` : ""} • {fmtDate(b.created_at)}
                      {isAdmin && b.owner_name ? ` • către ${b.owner_name}` : ""}
                      {isAdmin && b.imported_by_name ? ` • de ${b.imported_by_name}` : ""}
                    </div>
                  </div>
                  {!b.undone && (
                    <Button variant="outline" size="sm" className="shrink-0 text-red-400 hover:text-red-300"
                      onClick={() => undo(b)} disabled={busyId === b.id} data-testid={`undo-import-${b.id}`}>
                      <Trash size={14} className="mr-1" /> {busyId === b.id ? "…" : "Anulează"}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
